import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { BOOKING_AGENCY } from '@/data/events';
import type { Resident } from '@/types';

import styles from './BookingForm.module.css';

const schema = z.object({
  name: z.string().trim().min(1, 'REQUIRED'),
  org: z.string().trim().optional().default(''),
  email: z.string().trim().email('INVALID EMAIL'),
  date: z.string().trim().min(1, 'REQUIRED'),
  city: z.string().trim().optional().default(''),
  venue: z.string().trim().optional().default(''),
  capacity: z.string().optional().default(''),
  budget: z.string().optional().default(''),
  message: z.string().trim().min(10, 'TOO SHORT'),
  hp: z.string().max(0, 'BOT'),
});

type BookingFormValues = z.infer<typeof schema>;

interface BookingFormProps {
  artist: Pick<Resident, 'name'> | null;
  mode?: 'modal' | 'inline';
  onClose?: () => void;
}

export default function BookingForm({ artist, mode = 'modal', onClose }: BookingFormProps) {
  const [ref, setRef] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<BookingFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      org: '',
      email: '',
      date: '',
      city: '',
      venue: '',
      capacity: '',
      budget: '',
      message: '',
      hp: '',
    },
  });

  useEffect(() => {
    if (mode !== 'modal') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mode, onClose]);

  const onSubmit = handleSubmit(() => {
    const n = Math.floor(Math.random() * 9000) + 1000;
    setRef(`M-BOOK-${n}`);
  });

  const Body = (
    <>
      <div className={styles.shellHead}>
        <span className={styles.ttl}>/ BOOKING REQUEST · {BOOKING_AGENCY.name}</span>
        {mode === 'modal' && (
          <button type="button" onClick={onClose} aria-label="Close">
            ×
          </button>
        )}
      </div>

      {ref ? (
        <div className={styles.success}>
          <span className={styles.stamp}>◦ TRANSMISSION RECEIVED</span>
          <h3>REQUEST LOGGED.</h3>
          <p>
            Thank you. Your booking inquiry for <b>{artist?.name ?? '—'}</b> has been forwarded to{' '}
            {BOOKING_AGENCY.name}. {BOOKING_AGENCY.reply}.
          </p>
          <div className={styles.ref}>
            <span>REF</span>
            <b>{ref}</b>
          </div>
          {mode === 'modal' && (
            <div
              className={styles.foot}
              style={{ width: '100%', justifyContent: 'flex-end' }}
            >
              <div className={styles.actions}>
                <button type="button" className={styles.primary} onClick={onClose}>
                  CLOSE
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <form className={styles.form} onSubmit={onSubmit} noValidate>
          <div className={styles.forRow}>
            <div className={styles.labelStack}>
              <span className={styles.lbl}>REQUESTING TALENT</span>
              <span className={styles.who}>{artist?.name ?? '—'}</span>
            </div>
            <span className={styles.agency}>
              VIA {BOOKING_AGENCY.name}
              <br />
              {BOOKING_AGENCY.email}
            </span>
          </div>

          <div className={styles.grid}>
            <div className={styles.field}>
              <label htmlFor="bf-name">
                YOUR NAME <span className={styles.req}>*</span>
              </label>
              <input
                id="bf-name"
                placeholder="FULL NAME"
                aria-invalid={Boolean(errors.name)}
                {...register('name')}
              />
              {errors.name && <span className={styles.err}>{errors.name.message}</span>}
            </div>
            <div className={styles.field}>
              <label htmlFor="bf-org">ORGANISATION</label>
              <input
                id="bf-org"
                placeholder="LABEL / VENUE / PROMOTER"
                {...register('org')}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="bf-email">
                EMAIL <span className={styles.req}>*</span>
              </label>
              <input
                id="bf-email"
                type="email"
                placeholder="YOU@DOMAIN"
                aria-invalid={Boolean(errors.email)}
                {...register('email')}
              />
              {errors.email && <span className={styles.err}>{errors.email.message}</span>}
            </div>
            <div className={styles.field}>
              <label htmlFor="bf-date">
                EVENT DATE(S) <span className={styles.req}>*</span>
              </label>
              <input
                id="bf-date"
                placeholder="2026-08-15 / RANGE OK"
                aria-invalid={Boolean(errors.date)}
                {...register('date')}
              />
              {errors.date && <span className={styles.err}>{errors.date.message}</span>}
            </div>
            <div className={styles.field}>
              <label htmlFor="bf-city">CITY</label>
              <input id="bf-city" placeholder="BERLIN / TBILISI / …" {...register('city')} />
            </div>
            <div className={styles.field}>
              <label htmlFor="bf-venue">VENUE</label>
              <input id="bf-venue" placeholder="VENUE NAME" {...register('venue')} />
            </div>
            <div className={styles.field}>
              <label htmlFor="bf-capacity">CAPACITY</label>
              <select id="bf-capacity" {...register('capacity')}>
                <option value="">— SELECT —</option>
                <option>{'< 200'}</option>
                <option>200 – 500</option>
                <option>500 – 1000</option>
                <option>1000 – 2500</option>
                <option>{'> 2500'}</option>
              </select>
            </div>
            <div className={styles.field}>
              <label htmlFor="bf-budget">BUDGET RANGE (EUR)</label>
              <select id="bf-budget" {...register('budget')}>
                <option value="">— SELECT —</option>
                <option>{'< 1k'}</option>
                <option>1 – 3k</option>
                <option>3 – 6k</option>
                <option>6 – 10k</option>
                <option>{'> 10k'}</option>
              </select>
            </div>
            <div className={`${styles.field} ${styles.span2}`}>
              <label htmlFor="bf-msg">
                MESSAGE <span className={styles.req}>*</span>
              </label>
              <textarea
                id="bf-msg"
                placeholder="Format, lineup context, set length, technical notes…"
                aria-invalid={Boolean(errors.message)}
                {...register('message')}
              />
              {errors.message && <span className={styles.err}>{errors.message.message}</span>}
            </div>
            <input
              className={styles.hp}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              {...register('hp')}
            />
          </div>

          <div className={styles.foot}>
            <span className={styles.note}>RATES NOT LISTED · INQUIRIES REVIEWED</span>
            <div className={styles.actions}>
              {mode === 'modal' && (
                <button type="button" onClick={onClose}>
                  CANCEL
                </button>
              )}
              <button className={styles.primary} type="submit" disabled={isSubmitting}>
                SEND REQUEST →
              </button>
            </div>
          </div>
        </form>
      )}
    </>
  );

  if (mode === 'inline') {
    return (
      <section className={styles.inline} data-screen-label="booking form inline">
        {Body}
      </section>
    );
  }

  return (
    <div
      className={styles.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className={styles.shell}
        role="dialog"
        aria-modal="true"
        data-screen-label="booking form modal"
      >
        {Body}
      </div>
    </div>
  );
}
